(() => {
  if (customElements.get('lello-discovery')) return;
  const key = 'lello_recently_viewed_v1';
  const lifetime = 30 * 24 * 60 * 60 * 1000;
  const maxHistory = 12;
  const cache = new Map();
  let memory = [];
  const valid = item => item && typeof item.handle === 'string' && item.handle.length > 0 && item.handle.length < 300 && !/[/?#<>\x00-\x1f]/.test(item.handle) && Number.isFinite(item.at) && item.at > Date.now() - lifetime && item.at <= Date.now() + 60000;
  function readHistory() {
    let data = memory;
    try { data = JSON.parse(localStorage.getItem(key) || '[]'); } catch {
      try { data = JSON.parse(sessionStorage.getItem(key) || '[]'); } catch {}
    }
    if (!Array.isArray(data)) data = [];
    const seen = new Set();
    return data.filter(item => valid(item) && !seen.has(item.handle) && seen.add(item.handle)).slice(0, maxHistory);
  }
  function saveHistory(data) {
    memory = data;
    try { localStorage.setItem(key, JSON.stringify(data)); } catch {
      try { sessionStorage.setItem(key, JSON.stringify(data)); } catch {}
    }
  }
  function clearHistory() {
    memory = [];
    try { localStorage.removeItem(key); } catch {}
    try { sessionStorage.removeItem(key); } catch {}
  }
  class LelloDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.life = new AbortController();
      this.recentVersion = 0;
      this.rootUrl = new URL(this.dataset.rootUrl || '/', location.origin);
      this.currentHandle = this.dataset.currentHandle || '';
      this.onClick = event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-ld-clear')) {
          clearHistory();
          this.recentVersion++;
          this.querySelector('[data-ld-recent]').hidden = true;
          this.querySelector('[data-ld-recent] .ld-track').replaceChildren();
          this.querySelector('[data-ld-status]').textContent = this.dataset.clearedLabel;
          return;
        }
        const row = button.closest('.ld-row');
        if (!row) return;
        const track = row.querySelector('.ld-track');
        const direction = button.hasAttribute('data-ld-previous') ? -1 : button.hasAttribute('data-ld-next') ? 1 : 0;
        if (direction) track.scrollBy({left:direction * track.clientWidth, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      };
      this.addEventListener('click', this.onClick);
      this.cartListener = () => {
        cache.clear();
        clearTimeout(this.cartTimer);
        this.cartTimer = setTimeout(() => this.refreshSuggestions(), 80);
      };
      document.addEventListener('lc:cart-updated', this.cartListener);
      this.storageListener = event => { if (event.key === key || event.key === null) this.renderRecent(); };
      window.addEventListener('storage', this.storageListener);
      this.pageListener = event => { if (event.persisted) { cache.clear(); this.renderRecent(); this.refreshSuggestions(); } };
      window.addEventListener('pageshow', this.pageListener);
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this);
      this.addEventListener('scroll', () => this.updateControls(), {capture:true, signal:this.life.signal, passive:true});
      this.bindTracks();
      if (this.dataset.recentEnabled === 'true') {
        let history = readHistory();
        if (this.currentHandle) history = [{handle:this.currentHandle,at:Date.now()}, ...history.filter(item => item.handle !== this.currentHandle)].slice(0,maxHistory);
        saveHistory(history);
        this.renderRecent();
      }
    }
    disconnectedCallback() {
      this.life?.abort();
      this.request?.abort();
      this.recentVersion++;
      clearTimeout(this.cartTimer);
      this.resizeObserver?.disconnect();
      this.removeEventListener('click',this.onClick);
      document.removeEventListener('lc:cart-updated',this.cartListener);
      window.removeEventListener('storage',this.storageListener);
      window.removeEventListener('pageshow',this.pageListener);
      this.initialized = false;
    }
    bindTracks() {
      this.querySelectorAll('.ld-track').forEach(track => {
        if (track.dataset.ldBound) return;
        track.dataset.ldBound = 'true';
        track.addEventListener('keydown', event => {
          if (event.target !== track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home' || event.key === 'End') track.scrollTo({left:event.key === 'Home' ? 0 : track.scrollWidth});
          else track.scrollBy({left:(event.key === 'ArrowLeft' ? -1 : 1) * track.clientWidth});
        },{signal:this.life.signal});
      });
      this.updateControls();
    }
    updateControls() {
      this.querySelectorAll('.ld-row').forEach(row => {
        const track = row.querySelector('.ld-track');
        const controls = row.querySelector('[data-ld-controls]');
        if (!track || !controls) return;
        const overflow = track.scrollWidth > track.clientWidth + 4;
        controls.hidden = !overflow;
        track.tabIndex = overflow ? 0 : -1;
        controls.querySelector('[data-ld-previous]').disabled = track.scrollLeft <= 2;
        controls.querySelector('[data-ld-next]').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      });
    }
    async fetchCard(handle) {
      const url = new URL(`products/${encodeURIComponent(handle)}`,this.rootUrl);
      url.searchParams.set('section_id','lello-discovery-card');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id',preview);
      if (!cache.has(url.href)) cache.set(url.href,fetch(url,{credentials:'same-origin',signal:this.life.signal}).then(async response => {
        if (!response.ok) return null;
        const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
        const card = parsed.querySelector('[data-ld-fetched-card]');
        return card?.dataset.ldProduct === handle ? card : null;
      }).catch(() => null));
      const result = await cache.get(url.href);
      return result?.cloneNode(true);
    }
    async renderRecent() {
      const row = this.querySelector('[data-ld-recent]');
      if (!row) return;
      const version = ++this.recentVersion;
      const items = readHistory().filter(item => item.handle !== this.currentHandle).slice(0,8);
      if (!items.length) { row.hidden = true; row.querySelector('.ld-track').replaceChildren(); return; }
      const cards = [];
      for (let start=0; start<items.length; start+=3) {
        const chunk = await Promise.all(items.slice(start,start+3).map(item => this.fetchCard(item.handle)));
        cards.push(...chunk.filter(Boolean));
        if (version !== this.recentVersion || !this.isConnected) return;
      }
      const fragment = document.createDocumentFragment();
      cards.slice(0,6).forEach(card => {
        const li = document.createElement('li');
        li.className = 'ld-item';
        li.dataset.ldProduct = card.dataset.ldProduct;
        li.append(...card.childNodes);
        fragment.append(li);
      });
      row.querySelector('.ld-track').replaceChildren(fragment);
      row.hidden = !cards.length;
      this.bindTracks();
    }
    async refreshSuggestions() {
      if (!['product','cart'].includes(this.dataset.pageType)) return;
      this.request?.abort();
      this.request = new AbortController();
      const request = this.request;
      const url = new URL(location.href);
      url.searchParams.set('section_id',this.dataset.sectionId);
      try {
        const response = await fetch(url,{credentials:'same-origin',cache:'no-store',signal:request.signal});
        if (!response.ok) return;
        const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
        const incoming = parsed.querySelector('[data-ld-suggestions]');
        const current = this.querySelector('[data-ld-suggestions]');
        if (incoming && current && this.isConnected) { current.replaceWith(incoming); this.bindTracks(); }
      } catch {}
    }
  }
  customElements.define('lello-discovery',LelloDiscovery);
})();
